import { Stack, Text } from '@mantine/core'
import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import { ContractParams, useChainFunctions } from '@/hooks/useChain'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

interface RestakeConfirmationProps {
  close: () => void
  stakeDays: number
  selectedCompletedStakes: string
  stakeCallConfig: ContractParams | undefined
}

const RestakeConfirmation: React.FC<RestakeConfirmationProps> = ({
  close,
  stakeDays,
  selectedCompletedStakes,
  stakeCallConfig,
}) => {
  const { t } = useTranslation()

  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  // ready confirmation
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  const handleRestake = useCallback(async () => {
    if (!stakeCallConfig) return
    try {
      setIsLoading(true)
      await writeContract(stakeCallConfig)
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message)
    }
  }, [stakeCallConfig, writeContract])

  return (
    <>
      {error && (
        <ErrorMessage
          message={t('components.staking.staking.modal.stake.errorMessage', 'Error while staking')}
          details={error}
        />
      )}
      {isLoading && (
        <ConfirmationLoading
          title={t('components.staking.staking.modal.stake.loadingMessage', 'Validating Stake')}
        />
      )}
      {isReady && !isLoading && !error && (
        <ConfirmationReady
          message={t('components.staking.staking.modal.stake.readyMessage', 'Stake Submitted')}
          tip={t(
            'components.staking.staking.modal.stake.tipMessage',
            'Check Wallet History for Transaction Status'
          )}
          success={close}
        />
      )}
      {!isReady && !error && !isLoading && (
        <>
          <Text>
            <Trans i18nKey="components.staking.staking.modal.restake.confirmation">
              You are about to lock {{ amount: selectedCompletedStakes }} L1 for{' '}
              {{ numberStakeDays: stakeDays }} {{ stakeDays: stakeDays === 1 ? 'day' : 'days' }}.
              These L1 will not be accessible to you during the designated staking period.
            </Trans>
          </Text>
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={close} secondary style1={false} expand>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton
              disabled={!selectedCompletedStakes || !stakeDays}
              onClick={handleRestake}
              style1={false}
              expand
            >
              {t('buttons.confirm', 'Confirm')}
            </RootButton>
          </Stack>
        </>
      )}
    </>
  )
}

export default RestakeConfirmation
